import { useEffect, useMemo, useState } from "react";
import Loader from "../../components/loader/loader";
import {
    useGetNotificationSettingsQuery,
    useUpdateNotificationSettingMutation,
} from "../../services/store/api/notificationSettingsApi";
import styles from "./notification-settings.module.css";

export function NotificationSettingsPage() {
    const { data: notificationSettings = [], isLoading } = useGetNotificationSettingsQuery();
    const [updateNotificationSetting, { isLoading: isUpdating }] = useUpdateNotificationSettingMutation();
    const [draftSettings, setDraftSettings] = useState<Record<string, boolean>>({});
    const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "error">("idle");

    useEffect(() => {
        setDraftSettings(Object.fromEntries(
            notificationSettings.map((setting) => [setting.type, setting.is_enabled])
        ));
        setSaveStatus("idle");
    }, [notificationSettings]);

    const changedSettings = useMemo(() => {
        return notificationSettings.filter((setting) => (
            draftSettings[setting.type] !== undefined
            && draftSettings[setting.type] !== setting.is_enabled
        ));
    }, [draftSettings, notificationSettings]);

    const hasChanges = changedSettings.length > 0;

    const handleSave = async () => {
        if (!hasChanges || isUpdating) {
            return;
        }

        setSaveStatus("idle");

        try {
            await Promise.all(changedSettings.map((setting) => updateNotificationSetting({
                type: setting.type,
                is_enabled: draftSettings[setting.type],
            }).unwrap()));
            setSaveStatus("saved");
        } catch (error) {
            setSaveStatus("error");
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Уведомления</h1>

            <div className={styles.list}>
                {notificationSettings.map((setting) => (
                    <label className={styles.item} key={setting.type}>
                        <span className={styles.text}>
                            <span className={styles.itemTitle}>{setting.title}</span>
                            <span className={styles.description}>{setting.description}</span>
                        </span>
                        <input
                            type="checkbox"
                            checked={draftSettings[setting.type] ?? setting.is_enabled}
                            disabled={isUpdating}
                            onChange={(event) => {
                                setDraftSettings((current) => ({
                                    ...current,
                                    [setting.type]: event.target.checked,
                                }));
                                setSaveStatus("idle");
                            }}
                        />
                    </label>
                ))}
            </div>

            <div className={styles.actions}>
                <button
                    type="button"
                    className="btn-link btn-link-full"
                    disabled={!hasChanges || isUpdating}
                    onClick={handleSave}
                >
                    {isUpdating ? "Сохраняем..." : "Сохранить"}
                </button>
                {saveStatus === "saved" && <div className={styles.success}>Сохранено</div>}
                {saveStatus === "error" && <div className={styles.error}>Не удалось сохранить</div>}
            </div>
        </div>
    );
}